/**
 * Command-label program resolvers (ticket 3525, upstream pin e3e7cc3f, MIT),
 * split out of the ported `commandLabel.ts`. Byte-faithful.
 *
 * @module workLogCommandPrograms
 */

import {
  NON_DESCRIPTIVE_SHELL_PROGRAMS,
  NON_PROGRAM_PREFIX_CHARACTERS,
  NON_PROGRAM_SUFFIX_CHARACTERS,
  POWERSHELL_FLAGS,
  POWERSHELL_OPTIONS_WITH_VALUE,
  START_PROCESS_FLAGS,
  START_PROCESS_OPTIONS_WITH_VALUE,
} from "./workLogCommandTokens";
import { commandProgramNameInternal } from "./workLogCommandDispatcher";

// fallow-ignore-next-line complexity -- port of upstream commandLabel.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function transparentWrapperCommandIndex(
  wrapper: string,
  tokens: ReadonlyArray<string>,
  index: number,
): number | null {
  if (wrapper === "bundle") {
    return tokens[index + 1] === "exec" && tokens[index + 2] !== undefined ? index + 2 : null;
  }

  if (wrapper === "nohup") {
    let targetIndex = index + 1;
    if (tokens[targetIndex] === "--") targetIndex += 1;
    const target = tokens[targetIndex];
    return target && !target.startsWith("-") ? targetIndex : null;
  }

  if (wrapper === "script") {
    // BSD `script` takes an output file before the optional command. Requiring
    // an option and both operands avoids guessing about a plain `script file`.
    return /^-[adkpqr]+$/u.test(tokens[index + 1] ?? "") && tokens[index + 3] !== undefined
      ? index + 3
      : null;
  }

  if (wrapper === "arch") {
    if (/^-(?:arm64|arm64e|i386|x86_64)$/u.test(tokens[index + 1] ?? "")) {
      return tokens[index + 2] !== undefined ? index + 2 : null;
    }
    return tokens[index + 1] === "-arch" && tokens[index + 3] !== undefined ? index + 3 : null;
  }

  if (wrapper === "timeout" || wrapper === "gtimeout") {
    return /^(?:\d+(?:\.\d*)?|\.\d+)[smhd]?$/u.test(tokens[index + 1] ?? "") &&
      tokens[index + 2] !== undefined
      ? index + 2
      : null;
  }

  return null;
}

export function staticProgramName(value: string): string | null {
  const trimmedValue = value.trim();
  if (!trimmedValue || /^[A-Za-z][A-Za-z0-9+.-]*:(?![\\/])/u.test(trimmedValue)) return null;
  const program = trimmedValue.split(/[\\/]/u).at(-1);
  if (
    !program ||
    (/\s/u.test(program) && !/[\\/]/u.test(trimmedValue)) ||
    NON_PROGRAM_PREFIX_CHARACTERS.includes(program[0] ?? "") ||
    NON_PROGRAM_SUFFIX_CHARACTERS.includes(program.at(-1) ?? "")
  ) {
    return null;
  }
  return program;
}

// fallow-ignore-next-line complexity -- port of upstream commandLabel.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
function leadingPowerShellLiteral(command: string): string | null {
  const input = command.trimStart();
  const quote = input[0];
  if (quote !== '"' && quote !== "'") return staticProgramName(input.match(/^\S+/u)?.[0] ?? "");

  let value = "";
  for (let index = 1; index < input.length; index += 1) {
    const character = input[index]!;
    if (character === "`" && input[index + 1] !== undefined) {
      value += input[index + 1];
      index += 1;
      continue;
    }
    if (character === quote) {
      if (quote === "'" && input[index + 1] === "'") {
        value += "'";
        index += 1;
        continue;
      }
      return staticProgramName(value);
    }
    value += character;
  }
  return null;
}

export function powerShellCallOperatorProgramName(command: string): string | null | undefined {
  const match = command.match(/^\s*&\s+([\s\S]*)$/u);
  return match ? leadingPowerShellLiteral(match[1]!) : undefined;
}

type PowerShellAssignment = {
  readonly matched: boolean;
  readonly program: string | null;
};

export function powerShellAssignmentProgramName(
  command: string,
  depth: number,
  remainingCommand: string | null,
  segmentsRemaining: number,
): PowerShellAssignment {
  const assignment = command.match(
    /^\s*\$(?:(env|global|local|script):)?[A-Za-z_][A-Za-z0-9_]*\s*=\s*([\s\S]*)$/iu,
  );
  if (!assignment) return { matched: false, program: null };

  // Environment assignments are setup. Their right-hand side is a value, not
  // a command, so prefer the next top-level segment when one exists.
  if (assignment[1]?.toLowerCase() === "env") {
    return {
      matched: true,
      program: remainingCommand
        ? commandProgramNameInternal(remainingCommand, depth, "shell", segmentsRemaining - 1)
        : null,
    };
  }

  const value = assignment[2]!.trim();
  // The POSIX-oriented segment splitter does not balance PowerShell arrays or
  // hashtables. Do not mistake a key after an internal semicolon for a command.
  if (/^(?:\[ordered\]\s*)?@\s*[{(]/iu.test(value)) {
    return { matched: true, program: null };
  }
  const calledProgram = powerShellCallOperatorProgramName(value);
  if (calledProgram !== undefined) return { matched: true, program: calledProgram };

  const directCommand = value.match(/^(?:@?\(\s*)?([A-Za-z][A-Za-z0-9_.-]*)\b/u)?.[1];
  if (directCommand && !NON_DESCRIPTIVE_SHELL_PROGRAMS.has(directCommand.toLowerCase())) {
    const parsedCommand = commandProgramNameInternal(value, depth + 1, "shell", segmentsRemaining);
    return { matched: true, program: parsedCommand ?? directCommand };
  }

  return {
    matched: true,
    program: remainingCommand
      ? commandProgramNameInternal(remainingCommand, depth, "shell", segmentsRemaining - 1)
      : null,
  };
}

type WindowsShellPayload = {
  readonly matched: boolean;
  readonly program: string | null;
};

// fallow-ignore-next-line complexity -- port of upstream commandLabel.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
export function windowsShellPayloadProgramName(
  shell: string,
  tokens: ReadonlyArray<string>,
  start: number,
  depth: number,
  remainingCommand: string | null,
  separator: string | null,
  segmentsRemaining: number,
): WindowsShellPayload {
  const parsePayload = (payload: string | undefined): string | null => {
    if (!payload) return null;
    const command =
      remainingCommand && separator ? `${payload} ${separator} ${remainingCommand}` : payload;
    return commandProgramNameInternal(command, depth + 1, "shell", segmentsRemaining);
  };

  if (shell === "cmd" || shell === "cmd.exe") {
    for (let index = start; index < tokens.length; index += 1) {
      const option = tokens[index]!.toLowerCase();
      if (option !== "/c" && option !== "/k") continue;
      const payload = tokens[index + 1];
      return {
        matched: true,
        program: parsePayload(payload),
      };
    }
    return { matched: false, program: null };
  }

  for (let index = start; index < tokens.length; index += 1) {
    const option = tokens[index]!.toLowerCase();
    if (option === "-command" || option === "-c") {
      const payload = tokens[index + 1];
      return {
        matched: true,
        program: parsePayload(payload),
      };
    }
    if (option === "-file" || option === "-f") {
      return { matched: true, program: staticProgramName(tokens[index + 1] ?? "") };
    }
    if (option === "-encodedcommand" || option === "-enc" || option === "-e") {
      return { matched: true, program: null };
    }
    if (POWERSHELL_OPTIONS_WITH_VALUE.has(option)) {
      index += 1;
      continue;
    }
    if (POWERSHELL_FLAGS.has(option)) continue;
    if (!option.startsWith("-")) {
      return { matched: true, program: staticProgramName(tokens[index]!) };
    }
  }
  return { matched: false, program: null };
}

export function startProcessProgramName(
  tokens: ReadonlyArray<string>,
  start: number,
): string | null {
  for (let index = start; index < tokens.length; index += 1) {
    const token = tokens[index]!;
    const option = token.toLowerCase();
    if (option === "-filepath") return staticProgramName(tokens[index + 1] ?? "");
    if (START_PROCESS_FLAGS.has(option)) continue;
    if (START_PROCESS_OPTIONS_WITH_VALUE.has(option)) {
      if (tokens[index + 1] === undefined) return null;
      index += 1;
      continue;
    }
    if (token.startsWith("-")) return null;
    return staticProgramName(token);
  }
  return null;
}
